import CloseIcon from '@offline-mui/icons-material/Close'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@offline-mui/material'
import { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react'
import authContext from '../../context/auth-context'
import { accountingRequest, jsonInit, round2 } from '../api'
import type { DraftRows } from './InvoicePage'

// SND-14258: New Invoice screen. Line items and payments are added on screen with the same forms as an imported
// invoice; the summary is worked out here from what is entered, and Create invoice saves the header, lines and payments
// together in one request. Opened with an invoice made here, it edits that invoice's header (the PNR stays as it is).
const FLIGHT_TYPES = ['CAD', 'USD']
const PROFILE_URL = process.env.REACT_APP_BASE_URL || ''
// Agency search waits for typing to pause; short prefixes (e.g. "C6", 400+ agencies) take several seconds.
const AGENCY_SEARCH_DELAY_MS = 300
const FORM_ID = 'new-invoice-header'

const EMPTY_AGENCY = { code: '', name: '', phone: '', address: '', networkId: '', email: '' }
const POINTS_LINE = /^\d+\s+points$/i

type DraftRow = { key: string; payload: any; addedAt: string }

// A draft line in the shape the line table and line form read (the same shape the server returns).
function draftLineView(row: DraftRow) {
  const p = row.payload
  const txn = p.breakdown || {}
  const description = p.description || p.supplierName || ''
  return {
    isDraft: true,
    draftKey: row.key,
    lineIdentifier: `draft-${row.key}`,
    code: p.supplierCode,
    supplierName: p.supplierName,
    txnType: p.txnType,
    airlineCode: p.airlineCode,
    description,
    ticketNumber: p.ticketNumber,
    status: 'Open',
    date: p.date || row.addedAt,
    kind: POINTS_LINE.test(description.trim()) ? 'loyalty' : p.ticketNumber || p.airlineCode ? 'ticket' : 'fee',
    total: Number(txn.total) || 0,
    ourCommission: Number(p.ourCommissionBreakdown?.value) || 0,
    agencyCommission: Number(p.agencyCommissionBreakdown?.value) || 0,
    profit: Number(p.profit) || 0,
    breakdown: { base: txn.value, otherTaxes: txn.otherTaxes, gst: txn.gst, qst: txn.qst, hst: txn.hst, total: txn.total },
    ourCommissionBreakdown: p.ourCommissionBreakdown || {},
    agencyCommissionBreakdown: p.agencyCommissionBreakdown || {},
  }
}

// A draft payment in the shape the payments table and payment form read.
function draftPaymentView(row: DraftRow) {
  const p = row.payload
  const isCard = /^credit\s*cards?$/i.test(String(p.fopCategory || '').trim())
  const last4 = isCard && /^\d{4}$/.test(p.cardOrCheck || '') ? p.cardOrCheck : ''
  const first6 = isCard ? p.cardFirst6 || '' : ''
  return {
    isDraft: true,
    draftKey: row.key,
    id: `draft-${row.key}`,
    fopCode: p.fopCode,
    checkType: p.checkType,
    lineIdentifier: p.lineIdentifier || '',
    reference: p.reference || '',
    debit: Number(p.debit) || 0,
    credit: Number(p.credit) || 0,
    paidAt: row.addedAt,
    cardOrCheck: p.cardOrCheck || '',
    cardFirst6: first6,
    cardOrCheckDisplay: first6 && last4 ? `${first6.slice(0, 4)} ${first6.slice(4)}** **** ${last4}` : last4 ? `**** **** **** ${last4}` : p.cardOrCheck || '',
  }
}

// Same sums as the server's invoice summary: points lines are loyalty, not sales.
// Balance (SND-14328): payments are every row except RA (credit +, debit -), payable to agency is RA (debit +, credit -).
function draftSummary(lines: any[], payments: any[]) {
  let sales = 0
  let ourCommission = 0
  let agencyCommission = 0
  let intairProfit = 0
  let loyalty = 0
  let profit = 0
  for (const line of lines) {
    const points = POINTS_LINE.test(String(line.description || '').trim())
    if (points) loyalty += line.total
    else {
      sales += line.total
      intairProfit += line.profit
    }
    ourCommission += line.ourCommission
    agencyCommission += line.agencyCommission
    profit += line.profit
  }
  let paid = 0
  let payableToAgency = 0
  for (const payment of payments) {
    const net = payment.credit - payment.debit
    if (String(payment.fopCode || '').trim().toUpperCase() === 'RA') payableToAgency -= net
    else paid += net
  }
  return {
    sales: round2(sales),
    ourCommission: round2(ourCommission),
    agencyCommission: round2(agencyCommission),
    intairProfit: round2(intairProfit),
    loyaltyDollars: round2(-loyalty),
    profitAfterLoyalty: round2(profit),
    payments: round2(paid),
    payableToAgency: round2(payableToAgency),
    balance: round2(sales - paid - agencyCommission + payableToAgency),
  }
}

const EMPTY_HEADER = {
  flightType: 'CAD',
  officeCode: '',
  agent: '',
  recordLocator: '',
  provider: '',
  departureDate: '',
  ticketingDate: '',
  promoCode: '',
  pcc: '',
  origin: '',
  destination: '',
}

type Header = typeof EMPTY_HEADER
type Agency = typeof EMPTY_AGENCY

function dateOnly(value: unknown) {
  return String(value || '').slice(0, 10)
}

function headerFromInvoice(invoice: any): Header {
  return {
    flightType: invoice.flightType || 'CAD',
    officeCode: invoice.office?.code || '',
    agent: invoice.userName || '',
    recordLocator: invoice.recordLocator || '',
    provider: invoice.providerName || '',
    departureDate: dateOnly(invoice.departureDate),
    ticketingDate: dateOnly(invoice.ticketedAt),
    promoCode: invoice.promoCode || '',
    pcc: invoice.pcc || '',
    origin: invoice.origin || '',
    destination: invoice.destination || '',
  }
}

// Agency details from a GiGi travel business record, in the shape the invoice keeps.
function agencyFromTravelBusiness(record: any): Agency {
  return {
    code: String(record?.adAgencyCode || '').trim(),
    name: String(record?.adTravelAgencyName || record?.searchTravelBusinessesName || '').trim(),
    phone: String(record?.ciContactNumber || '').trim(),
    address: [record?.adAddressLine1, record?.adCity, record?.provinceOrState, record?.adPostalCode, record?.country]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(', '),
    networkId: String(record?.adNetworkId || '').trim(),
    email: String(record?.ciEmailAirSchedule || record?.ciEmail || '').trim(),
  }
}

function missingFields(header: Header, agency: Agency) {
  const labels: [string, string][] = [
    ['Flight type', header.flightType],
    ['Office', header.officeCode],
    ['Agency', agency.code && agency.name],
    ['Agent', header.agent.trim()],
    ['PNR', header.recordLocator.trim()],
    ['Provider', header.provider],
    ['Departure date', header.departureDate],
    ['Ticketing date', header.ticketingDate],
    ['PCC', header.pcc.trim()],
    ['Origin', header.origin.trim()],
    ['Destination', header.destination.trim()],
  ]
  return labels.filter(([, value]) => !value).map(([label]) => label)
}

function AgencyDetails({ agency }: { agency: Agency }) {
  const rows: [string, string][] = [
    ['Name', agency.name],
    ['Code', agency.code],
    ['Phone', agency.phone],
    ['Address', agency.address],
    ['Network ID', agency.networkId],
    ['Email', agency.email],
  ]
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 0.75, columnGap: 1.5 }}>
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ display: 'contents' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{value || '—'}</Typography>
        </Box>
      ))}
    </Box>
  )
}

export default function NewInvoiceDialog({
  open,
  invoice,
  onClose,
  onInvoiceChange,
  onExisting,
  onCreated,
  renderSummary,
  renderActivity,
}: {
  open: boolean
  // An invoice made on this screen, to edit its header; null for a new invoice.
  invoice: any | null
  onClose: () => void
  onInvoiceChange: (invoice: any) => void
  // The PNR is already invoiced: the page opens that invoice.
  onExisting: (invoice: any) => void
  // A new invoice was saved: the page opens it.
  onCreated: (invoice: any) => void
  renderSummary: (invoice: any | null) => ReactNode
  renderActivity: (invoice: any | null, onChange: (invoice: any) => void, draft?: DraftRows) => ReactNode
}) {
  const { orgId } = useContext(authContext) as any
  const [saved, setSaved] = useState<any>(null)
  const [header, setHeader] = useState<Header>({ ...EMPTY_HEADER })
  const [agency, setAgency] = useState<Agency>({ ...EMPTY_AGENCY })
  const [agencyOptions, setAgencyOptions] = useState<any[]>([])
  const [agencyQuery, setAgencyQuery] = useState('')
  const [agencyLoading, setAgencyLoading] = useState(false)
  const agencyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const agencyRequest = useRef<AbortController | null>(null)
  const [offices, setOffices] = useState<any[]>([])
  const [providers, setProviders] = useState<string[]>([])
  // PCCs of the chosen office (its accounts on the Offices page); null while loading.
  const [officePccs, setOfficePccs] = useState<string[] | null>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [draftLines, setDraftLines] = useState<DraftRow[]>([])
  const [draftPayments, setDraftPayments] = useState<DraftRow[]>([])
  const nextKey = useRef(1)

  useEffect(() => {
    if (!open) return
    setSaved(invoice || null)
    setHeader(invoice ? headerFromInvoice(invoice) : { ...EMPTY_HEADER })
    setAgency(invoice ? { ...EMPTY_AGENCY, ...invoice.agency, code: invoice.agency?.code || invoice.agencyCode || '' } : { ...EMPTY_AGENCY })
    setAgencyOptions([])
    setDraftLines([])
    setDraftPayments([])
    setError('')
    accountingRequest('/offices')
      .then((body) => setOffices(body.offices || []))
      .catch(() => setOffices([]))
  }, [open, invoice])

  useEffect(() => {
    if (!open) return
    let current = true
    accountingRequest(`/providers?flightType=${encodeURIComponent(header.flightType)}`)
      .then((body) => {
        if (current) setProviders((body.providers || []).map((provider: any) => provider.providerName))
      })
      .catch((err) => {
        if (current) {
          setProviders([])
          setError(err.message)
        }
      })
    return () => {
      current = false
    }
  }, [open, header.flightType])

  // PCC list follows the office. A PCC that is not one of the new office's is cleared; a single PCC is picked.
  useEffect(() => {
    if (!open) return
    const code = header.officeCode
    if (!code) {
      setOfficePccs([])
      return
    }
    let current = true
    setOfficePccs(null)
    accountingRequest(`/offices/${encodeURIComponent(code)}`)
      .then((office) => {
        if (!current) return
        const pccs = Array.from(
          new Set<string>((office?.accounts || []).map((account: any) => String(account?.pcc || '').trim().toUpperCase()).filter(Boolean)),
        )
        setOfficePccs(pccs)
        setHeader((h) => {
          if (h.pcc && pccs.includes(h.pcc)) return h
          if (invoice && h.pcc === (invoice.pcc || '') && h.officeCode === (invoice.office?.code || '')) return h
          return { ...h, pcc: pccs.length === 1 ? pccs[0] : '' }
        })
      })
      .catch(() => {
        if (current) setOfficePccs([])
      })
    return () => {
      current = false
    }
  }, [open, header.officeCode, invoice])

  // A saved invoice may hold a PCC that is no longer on its office; keep it selectable.
  const pccOptions = useMemo(() => {
    const list = officePccs || []
    return header.pcc && !list.includes(header.pcc) ? [header.pcc, ...list] : list
  }, [officePccs, header.pcc])

  // An invoice saved earlier may name a provider that is no longer enabled; keep it selectable.
  const providerOptions = useMemo(
    () => (header.provider && !providers.includes(header.provider) ? [header.provider, ...providers] : providers),
    [providers, header.provider],
  )

  function setField(key: keyof Header, value: string) {
    setHeader((current) => ({ ...current, [key]: value }))
  }

  // Stop a pending or running agency search (new text typed, or the window closed).
  function cancelAgencySearch() {
    if (agencyTimer.current) clearTimeout(agencyTimer.current)
    agencyTimer.current = null
    agencyRequest.current?.abort()
    agencyRequest.current = null
  }

  useEffect(() => cancelAgencySearch, [open])

  // Each keystroke used to send its own search, and a slow reply for a shorter code (C6) could arrive after the
  // reply for the full code (C6080) and replace it. Now only the latest text is searched; older searches are cancelled.
  function searchAgencies(value: string) {
    const code = value.trim().toUpperCase()
    setAgencyQuery(code)
    cancelAgencySearch()
    if (code.length < 2 || !orgId) {
      setAgencyOptions([])
      setAgencyLoading(false)
      return
    }
    setAgencyLoading(true)
    agencyTimer.current = setTimeout(async () => {
      const controller = new AbortController()
      agencyRequest.current = controller
      try {
        const response = await fetch(
          `${PROFILE_URL}/travelBusinesses/autoSuggest?orgId=${encodeURIComponent(orgId)}&adAgencyCode=${encodeURIComponent(code)}`,
          { signal: controller.signal },
        )
        const body = await response.json().catch(() => [])
        if (agencyRequest.current !== controller) return
        setAgencyOptions(Array.isArray(body) ? body : [])
      } catch {
        if (agencyRequest.current !== controller) return
        setAgencyOptions([])
      }
      agencyRequest.current = null
      setAgencyLoading(false)
    }, AGENCY_SEARCH_DELAY_MS)
  }

  async function pickAgency(option: any) {
    if (!option) {
      setAgency({ ...EMPTY_AGENCY })
      return
    }
    setAgency(agencyFromTravelBusiness(option))
    try {
      const response = await fetch(
        `${PROFILE_URL}/travelBusinesses?travelBusinessesId=${encodeURIComponent(option.travelBusinessesId)}&orgId=${encodeURIComponent(orgId)}`,
      )
      const record = await response.json()
      if (record?.adAgencyCode) setAgency(agencyFromTravelBusiness(record))
    } catch {
      // The code and name from the suggestion are enough to save.
    }
  }

  async function save(event: any) {
    event.preventDefault()
    const missing = missingFields(header, agency)
    if (missing.length) {
      setError(`Fill in: ${missing.join(', ')}.`)
      return
    }
    if (!/^[A-Za-z0-9]{6}$/.test(header.recordLocator.trim())) {
      setError('PNR must be 6 letters or digits.')
      return
    }
    if (!/^[A-Za-z]{3}$/.test(header.origin.trim()) || !/^[A-Za-z]{3}$/.test(header.destination.trim())) {
      setError('Origin and destination must be 3-letter airport codes.')
      return
    }
    setSaving(true)
    setError('')
    const payload = {
      ...header,
      recordLocator: header.recordLocator.trim().toUpperCase(),
      agency,
      lines: saved ? undefined : draftLines.map((row) => ({ ...row.payload, draftKey: row.key })),
      payments: saved
        ? undefined
        : draftPayments.map((row) => {
            const { lineIdentifier, fopCategory, ...rest } = row.payload
            const lineKey = String(lineIdentifier || '').startsWith('draft-') ? String(lineIdentifier).slice('draft-'.length) : undefined
            return { ...rest, lineKey, paidAt: row.addedAt }
          }),
    }
    try {
      if (saved) {
        const updated = await accountingRequest(`/invoices/${encodeURIComponent(saved.recordLocator)}/header`, jsonInit('PUT', payload))
        setSaving(false)
        onInvoiceChange(updated)
        onClose()
      } else {
        const result = await accountingRequest('/invoices/manual', jsonInit('POST', payload))
        setDraftLines([])
        setDraftPayments([])
        // The page takes over: it opens the new invoice (or the one this PNR already has) and this window unmounts,
        // so nothing is set here after the hand-off.
        if (result.alreadyExists) onExisting(result.invoice)
        else onCreated(result.invoice)
      }
    } catch (err: any) {
      setError(err.message)
      setSaving(false)
    }
  }

  function newRow(payload: any): DraftRow {
    const key = String(nextKey.current)
    nextKey.current += 1
    return { key, payload, addedAt: new Date().toISOString() }
  }

  // Line items and payments entered before the invoice exists.
  const draftRows: DraftRows = {
    addLine: (payload) => setDraftLines((rows) => [...rows, newRow(payload)]),
    // Editing a line does not send a date; keep the one it was added with.
    updateLine: (key, payload) =>
      setDraftLines((rows) => rows.map((row) => (row.key === key ? { ...row, payload: { ...payload, date: payload.date || row.payload.date } } : row))),
    removeLine: (key) => {
      setDraftLines((rows) => rows.filter((row) => row.key !== key))
      // A payment linked to the removed line stays, without the link.
      setDraftPayments((rows) =>
        rows.map((row) => (row.payload.lineIdentifier === `draft-${key}` ? { ...row, payload: { ...row.payload, lineIdentifier: undefined } } : row)),
      )
    },
    addPayment: (payload) => setDraftPayments((rows) => [...rows, newRow(payload)]),
    updatePayment: (key, payload) => setDraftPayments((rows) => rows.map((row) => (row.key === key ? { ...row, payload } : row))),
    removePayment: (key) => setDraftPayments((rows) => rows.filter((row) => row.key !== key)),
  }

  const draftInvoice = useMemo(() => {
    const lines = draftLines.map(draftLineView)
    const payments = draftPayments.map(draftPaymentView)
    return {
      recordLocator: header.recordLocator,
      invoiceNumber: '',
      flightType: header.flightType,
      supplierLines: lines,
      payments,
      summary: draftSummary(lines, payments),
    }
  }, [draftLines, draftPayments, header.recordLocator, header.flightType])

  function closeWindow() {
    const unsaved = !saved && (draftLines.length || draftPayments.length)
    if (unsaved && !window.confirm('Discard the line items and payments entered on this invoice?')) return
    onClose()
  }

  function onActivityChange(updated: any) {
    setSaved(updated)
    onInvoiceChange(updated)
  }

  const title = invoice ? `Edit invoice ${invoice.invoiceNumber}` : 'New Invoice'

  return (
    <Dialog open={open} onClose={closeWindow} fullScreen>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          px: 3,
          py: 1.5,
          color: 'common.white',
          background: 'linear-gradient(115deg, #0b6e6a 0%, #1d4e89 58%, #5b4b8a 100%)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
        <IconButton aria-label="Close" onClick={closeWindow} sx={{ ml: 'auto', color: 'common.white' }}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <DialogContent sx={{ bgcolor: '#f5f7fb' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 0.8fr' }, gap: 2 }}>
            <Card component="form" id={FORM_ID} onSubmit={save} sx={{ borderTop: '4px solid', borderTopColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>Invoice header</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>All fields are required except the loyalty promo code.</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField select size="small" label="Flight type" value={header.flightType} onChange={(event) => setField('flightType', event.target.value)}>
                    {FLIGHT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </TextField>
                  <TextField select size="small" label="Office" value={header.officeCode} onChange={(event) => setField('officeCode', event.target.value)}>
                    {offices.map((office) => (
                      <MenuItem key={office.code} value={office.code}>{office.name || office.code}</MenuItem>
                    ))}
                  </TextField>
                  <Autocomplete
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                    options={agencyOptions}
                    // Only codes that start with what is typed, so a late reply cannot show other agencies.
                    filterOptions={(options) => options.filter((option: any) => String(option.adAgencyCode || '').toUpperCase().startsWith(agencyQuery))}
                    loading={agencyLoading}
                    loadingText="Searching…"
                    getOptionLabel={(option: any) => `${option.adAgencyCode || ''} - ${option.searchTravelBusinessesName || option.adTravelAgencyName || ''}`}
                    isOptionEqualToValue={(option: any, value: any) => option.adAgencyCode === value.adAgencyCode}
                    value={agency.code ? { adAgencyCode: agency.code, searchTravelBusinessesName: agency.name } : null}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') searchAgencies(value)
                    }}
                    onChange={(_, option) => pickAgency(option)}
                    noOptionsText={agencyQuery.length < 2 ? 'Type at least 2 characters of the agency code' : 'No agency with this code'}
                    renderInput={(params) => <TextField {...params} size="small" label="Agency code" />}
                  />
                  <TextField size="small" label="Agent" value={header.agent} onChange={(event) => setField('agent', event.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} />
                  <TextField
                    size="small"
                    label="PNR"
                    value={header.recordLocator}
                    disabled={Boolean(saved)}
                    onChange={(event) => setField('recordLocator', event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                    helperText={saved ? 'The PNR cannot change once the invoice is saved.' : undefined}
                  />
                  <TextField select size="small" label="Provider" value={providerOptions.includes(header.provider) ? header.provider : ''} onChange={(event) => setField('provider', event.target.value)}>
                    {providerOptions.map((name) => (
                      <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="PCC"
                    value={pccOptions.includes(header.pcc) ? header.pcc : ''}
                    onChange={(event) => setField('pcc', event.target.value)}
                    disabled={!header.officeCode || officePccs === null}
                    helperText={
                      !header.officeCode
                        ? 'Select an office first: the PCC list comes from that office.'
                        : officePccs === null
                          ? 'Loading the office PCCs…'
                          : officePccs.length === 0
                            ? 'This office has no PCC. Add one on the Offices page.'
                            : 'PCCs of the selected office (Offices page).'
                    }
                  >
                    {pccOptions.map((pcc) => (
                      <MenuItem key={pcc} value={pcc}>{pcc}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" type="date" label="Departure date" value={header.departureDate} onChange={(event) => setField('departureDate', event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
                  <TextField size="small" type="date" label="Ticketing date" value={header.ticketingDate} onChange={(event) => setField('ticketingDate', event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
                  <TextField size="small" label="Loyalty promo code (optional)" value={header.promoCode} onChange={(event) => setField('promoCode', event.target.value.slice(0, 50))} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField size="small" label="Origin" value={header.origin} onChange={(event) => setField('origin', event.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3))} />
                    <TextField size="small" label="Destination" value={header.destination} onChange={(event) => setField('destination', event.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3))} />
                  </Box>
                </Box>

                <Typography variant="overline" color="info.main" sx={{ display: 'block', mt: 2.5, fontWeight: 700 }}>Agency details</Typography>
                <AgencyDetails agency={agency} />

              </CardContent>
            </Card>
            {renderSummary(saved || draftInvoice)}
          </Box>
          {saved ? renderActivity(saved, onActivityChange) : renderActivity(draftInvoice, onActivityChange, draftRows)}
        </Box>
      </DialogContent>
      {/* Pinned under the scrolling content so Save invoice and Cancel stay in reach on long invoices. */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', px: 3, py: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ maxWidth: 1200, mx: 'auto', alignItems: 'center' }}>
          {error ? <Alert severity="error" sx={{ flex: 1, py: 0 }}>{error}</Alert> : <Box sx={{ flex: 1 }} />}
          <Button type="button" onClick={closeWindow} disabled={saving}>Cancel</Button>
          <Button type="submit" form={FORM_ID} variant="contained" color="warning" disabled={saving}>
            {saving ? 'Saving…' : 'Save invoice'}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  )
}
